module OnlineDPR.Application.Client.Report.Report

open System

open Elmish
open Fable.Core
open Fable.React
open Fable.React.Props

open OnlineDPR.Application.Shared
open OnlineDPR.Application.Client.Report.SidebarMenu
open OnlineDPR.Application.Client.Report.HeaderMenu
open OnlineDPR.Application.Client.Report.Table
open OnlineDPR.Application.Client.Report.Chart
open OnlineDPR.Application.Client.Report.EditDpr
open OnlineDPR.Application.Client.Report.Types
open OnlineDPR.Application.Client.Report.Helper
open OnlineDPR.Application.Client.Template
open OnlineDPR.Application.Client.Components
open OnlineDPR.Application.Client
open Fable.Import

let logger = Jsnlog.GetLoggerWithName "Report"

[<Emit("bindMetisMenu()")>]
let bindMetisMenu(): unit = jsNative

let mutable initialLoadRequested : bool = false

let fetchReportData filter = Http.getData<ReportViewModel> (sprintf "/Report/GetData?FromYear=%i&FromMonth=%i&FromDay=%i&Level=%A&LevelIdentifier=%i&Interval=%A"
                                                                    filter.From.Year filter.From.Month filter.From.Day filter.Level filter.LevelIdentifier filter.Interval)
let loadData filter =
    Cmd.OfPromise.either
        fetchReportData
        filter
        (ParseModelData)
        ((fun e -> Error (string e)) >> ParseModelData)

let rec updateCategories (oldCategories : ReportCategory list) (newCategories : ReportCategory list) =
    oldCategories
    |> List.map (fun oldCategory -> let matchingNewCategory = ListHelper.search (fun item -> oldCategory.TranslationKey = item.TranslationKey ) newCategories
                                    match matchingNewCategory with
                                    | Some matchingNewCategory -> { oldCategory with DataRow = matchingNewCategory.DataRow
                                                                                     AggregatedAverageValue = matchingNewCategory.AggregatedAverageValue
                                                                                     AggregatedSumValue = matchingNewCategory.AggregatedSumValue
                                                                                     Subcategories = updateCategories oldCategory.Subcategories matchingNewCategory.Subcategories}
                                    | None -> logger.error("Failed updating report with new values. The translationkey has no matchting value!")
                                              oldCategory )

let updateModel oldModel newModelData =
    match oldModel.Count with
    | 0 -> { oldModel with Data = newModelData
                           Count = newModelData.Columns.Length}
    | _ -> { oldModel with Data = { oldModel.Data with Columns = newModelData.Columns
                                                       Categories = updateCategories oldModel.Data.Categories newModelData.Categories}
                           Count = newModelData.Columns.Length}

let validateEditRowFields (m:EditRowModel) =
    { m with
        ClubNumber = validateField m.ClubNumber
        Date = validateField m.Date }

let editDprRequestCmd (data:EditDprRequest) =
    Cmd.OfPromise.either
        (Http.postData APIUrls.PostEditDprRequest)
        data
        (EditDprResponded)
        (Http.toErrorMessage >> EditDprResponded)

let update msg model =
    match msg with
    | ParseModelData(Ok data) -> { updateModel model data with Updating = false }, []
    | ParseModelData(Error data) -> handleError data
                                    { model with Updating = false } , []
    | ParseMenu(Ok data) -> { model with Menu = data }, []
    | ParseMenu(Error data) -> handleError data
                               model, []
    | BindMenu ->   bindMetisMenu() |> ignore
                    model, []
    | ToggleCategoryVisibility category -> updateCategoryInModel model category { category with SubcategoriesVisible = (not category.SubcategoriesVisible) }, []
    | ToggleCategoryCheckbox category -> updateCategoryInModel model category { category with IncludeInReport = (not category.IncludeInReport) }, []
    | UpdateFilter filter -> {model with Filter = filter}, Cmd.ofMsg ReloadModel
    | ReloadModel -> initialLoadRequested <- true
                     let cmd = loadData model.Filter
                     { model with Updating = true}, cmd
    | UpdateChartOptions newOptions -> { model with ChartOptions = newOptions}, []
    | EditDprClubSelected clubNumber -> { model with EditDpr = {model.EditDpr with ClubNumber = clubNumber; ErrorMessage = None } }, []
    | EditDprDateSelected date -> let editDpr = validateEditRowFields {model.EditDpr with Date = date; ErrorMessage = None }
                                  { model with EditDpr = editDpr }, []
    | EditDprRequested -> let model =  { model with EditDpr = validateEditRowFields model.EditDpr }
                          let validationMessage = List.tryFind (fun x -> Option.isSome x.ValidationMessage) [ model.EditDpr.ClubNumber; model.EditDpr.Date]
                          match validationMessage with
                          | Some _ -> model, []
                          | None -> { model with EditDpr = { model.EditDpr with PendingRequest = true } },
                                    let parsedDate = DateTime.Parse model.EditDpr.Date.Value
                                    (editDprRequestCmd {
                                        ClubId = int model.EditDpr.ClubNumber.Value;
                                        SelectedDay = {Year = parsedDate.Year; Month = parsedDate.Month; Day = parsedDate.Day }})
    | EditDprResponded result -> match result with
                                 | Ok (EditRequestSuccess r) -> Browser.Dom.window.location.href <- r.Url
                                                                model, []
                                 | Ok (EditRequestError s) -> { model with EditDpr = { model.EditDpr with ErrorMessage = Some s
                                                                                                          PendingRequest = false} } , []
                                 | _ ->  model, []
    | ToggleShowEditDpr -> { model with ShowEditDpr = not model.ShowEditDpr }, []
    | ToggleShowChart -> { model with ShowChart = not model.ShowChart }, []

let view model dispatch =
    let headerMenu = renderHeader model dispatch
    let sidebarMenu = renderSidebar model dispatch
    let content = article [ Class "content"]
                          [ section [   Class "section" ]
                                    (match List.isEmpty model.Data.Categories with
                                    | true -> [ div [ ClassName "initial-loading-container"
                                                      Ref (fun _ -> match initialLoadRequested with
                                                                    | false -> dispatch ReloadModel
                                                                    | true -> ()) ]
                                                    [ spinner ] ]
                                    | false -> [ div [ Id "EditDpr" ]
                                                     [ renderEditDpr model dispatch ]
                                                 div [ Id "ReportTable" ]
                                                     [ renderPageContent model.Data dispatch model.Updating ]
                                                 div [ Id "ReportChart" ]
                                                     [ renderChart model dispatch ] ] ) ]
    templateView model.Template headerMenu sidebarMenu content